'use client';

import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

// `bottom-start` opens below with the bubble's left edge on the trigger's, for
// a trigger near the left edge of the viewport or a clipping container (a table
// header inside TableScrollRegion), where a centred bubble would lose its left side.
export type TooltipPlacement = 'top' | 'right' | 'bottom' | 'bottom-start' | 'left';

export interface TooltipProps {
  content: ReactNode;
  placement?: TooltipPlacement;
  /**
   * Switches the bubble off without changing the DOM (the wrapper span stays, so the child is not
   * remounted). For a caller that only has something to say sometimes, e.g. TruncatedText, which
   * tooltips only text that is actually clamped. A natively disabled child is then not given the
   * focusable stand-in either, since there is nothing to reach. Default false.
   */
  disabled?: boolean;
  /**
   * Extra classes for the wrapper span (`relative inline-block` stays). For a caller whose wrapper
   * is a flex item and has to say how it shrinks, e.g. the page header title's `min-w-0`.
   */
  className?: string;
  children: ReactElement<{
    'aria-describedby'?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
  }>;
}

const GAP = 4; // trigger-to-bubble space (the old mb-1 / mt-1 / ml-1 / mr-1)
const GUTTER = 8; // viewport-edge margin the bubble is kept inside

export interface TooltipPosition {
  top: number;
  left: number;
}

// Like Math.min/max clamp, but a range that is too small to hold the bubble
// (min > max) resolves to `min`, so an oversized bubble pins to the gutter
// instead of a negative offset.
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Viewport coordinates for the `position: fixed` bubble, from the trigger's and
 * the bubble's own rects. The requested side wins when it fits; otherwise the
 * opposite side is used if THAT fits; if neither fits the requested side is kept.
 * Both axes are then clamped inside the viewport (8px gutter). Same approach as
 * ActionMenu's `computePosition` (flip, then clamp).
 */
export function computeTooltipPosition(
  anchor: DOMRect,
  bubble: DOMRect,
  placement: TooltipPlacement,
  viewport: { width: number; height: number },
): TooltipPosition {
  const maxLeft = viewport.width - GUTTER - bubble.width;
  const maxTop = viewport.height - GUTTER - bubble.height;

  if (placement === 'left' || placement === 'right') {
    const fitsRight = anchor.right + GAP + bubble.width <= viewport.width - GUTTER;
    const fitsLeft = anchor.left - GAP - bubble.width >= GUTTER;
    const onLeft = placement === 'left' ? fitsLeft || !fitsRight : !fitsRight && fitsLeft;
    const left = onLeft ? anchor.left - GAP - bubble.width : anchor.right + GAP;
    const centredTop = anchor.top + anchor.height / 2 - bubble.height / 2;
    return { top: clamp(centredTop, GUTTER, maxTop), left: clamp(left, GUTTER, maxLeft) };
  }

  const fitsBelow = anchor.bottom + GAP + bubble.height <= viewport.height - GUTTER;
  const fitsAbove = anchor.top - GAP - bubble.height >= GUTTER;
  const above = placement === 'top' ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
  const top = above ? anchor.top - GAP - bubble.height : anchor.bottom + GAP;
  const left =
    placement === 'bottom-start' ? anchor.left : anchor.left + anchor.width / 2 - bubble.width / 2;
  return { top: clamp(top, GUTTER, maxTop), left: clamp(left, GUTTER, maxLeft) };
}

// The box a `position: fixed` bubble is laid out against. `clientWidth/Height` exclude a
// classic scrollbar; `innerWidth/Height` include it, so using those would let the clamp park
// the bubble up to a scrollbar-width past the real edge, where its shrink-to-fit width is
// squeezed and the (now wrappable) text re-wraps taller than what was measured. jsdom reports
// 0 for the client sizes, hence the fallback.
function viewportSize(): { width: number; height: number } {
  const root = document.documentElement;
  return {
    width: root.clientWidth || window.innerWidth,
    height: root.clientHeight || window.innerHeight,
  };
}

export function Tooltip({
  content,
  placement = 'top',
  disabled = false,
  className,
  children,
}: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  // A bubble that is open when the tooltip becomes disabled closes, and stays closed if it is
  // enabled again while the pointer is elsewhere.
  const visible = open && !disabled;
  useLayoutEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  // useLayoutEffect (not useEffect): the bubble mounts off-screen on the render
  // that flips `open`, and is measured and moved in the same frame, before paint,
  // so it never flashes at the wrong spot. Same idiom as ActionMenu. `content` is a dep so a
  // bubble whose text changes while it is open (a disabled-reason that updates) is re-measured
  // and re-clamped instead of growing off its trigger or out of the viewport.
  useLayoutEffect(() => {
    if (!visible) {
      setPosition(null);
      return;
    }
    function recompute() {
      const anchor = anchorRef.current;
      const bubble = bubbleRef.current;
      if (!anchor || !bubble) return;
      const next = computeTooltipPosition(
        anchor.getBoundingClientRect(),
        bubble.getBoundingClientRect(),
        placement,
        viewportSize(),
      );
      // Keep the previous object when nothing moved: the scroll listener below fires for a
      // scroll anywhere on the page, and a fresh object each time re-renders the trigger too.
      setPosition((prev) =>
        prev && prev.top === next.top && prev.left === next.left ? prev : next,
      );
    }
    recompute();
    window.addEventListener('resize', recompute);
    // Capture: a scroll on ANY ancestor (a table's scroll region) moves the trigger.
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [visible, placement, content]);

  const child = Children.only(children);
  if (!isValidElement(child)) return child;

  // A native `disabled` element never fires pointer or focus events -- cloning
  // the hover/focus handlers onto it (the pre-existing behavior below) is
  // inert, which is exactly why a disabled Send/Post button's `title` never
  // showed anything. The wrapping span isn't itself disabled and still
  // receives hover/focus from the browser, so route the handlers there
  // instead for a disabled child. Non-disabled children keep the original
  // clone-based wiring untouched -- zero behavior change for every other caller.
  //
  // Also checks `loading`: `@heyroot/ui`'s <Button> renders its native
  // element as `disabled={disabled || loading}`, so a Tooltip wrapping a
  // `<Button loading>` with no explicit `disabled` prop would otherwise be
  // natively disabled while this check still reported it as enabled --
  // cloning the (inert) handlers back onto the button and reproducing the
  // exact silent-tooltip bug this component exists to fix.
  // Assumes the child is a native form control or `@heyroot/ui`'s Button --
  // the only shapes this primitive is used with today (a custom component
  // that happens to have an unrelated `disabled`/`loading`-named prop would
  // be misread here; there are none in the codebase as of this writing).
  const childProps = child.props as { disabled?: boolean; loading?: boolean };
  const childIsDisabled = Boolean(childProps.disabled) || Boolean(childProps.loading);
  // Closing is never gated, so a leave or blur that happens while disabled is not missed.
  const openHandlers = {
    onMouseEnter: () => {
      if (!disabled) setOpen(true);
    },
    onMouseLeave: () => setOpen(false),
    onFocus: () => {
      if (!disabled) setOpen(true);
    },
    onBlur: () => setOpen(false),
  };

  const enhanced = cloneElement(child, {
    // Only the wrapper carries aria-describedby for a disabled child -- it's
    // the sole focusable, described element in that case. Also setting it on
    // the inert child would describe an element that's excluded from the
    // interaction (and, in some ATs, the reading) order, risking a double
    // announcement of the same reason text.
    ...(visible && !childIsDisabled ? { 'aria-describedby': id } : {}),
    ...(childIsDisabled ? {} : openHandlers),
  });

  const bubbleStyle: CSSProperties = {
    // Off-screen until measured (see the layout effect above).
    top: position?.top ?? -9999,
    left: position?.left ?? -9999,
    // Inline, so it beats the `pointer-events: none` a Radix modal puts on <body>:
    // this bubble is a body sibling that paints above the modal layer
    // (check-portal-overlay-modal-safety, #10161).
    pointerEvents: 'auto',
  };

  return (
    <span
      ref={anchorRef}
      className={['relative inline-block', className].filter(Boolean).join(' ')}
      // A disabled child can never receive focus itself -- make the wrapper
      // the keyboard-reachable stand-in so Tab still surfaces the tooltip.
      tabIndex={childIsDisabled && !disabled ? 0 : undefined}
      aria-describedby={childIsDisabled && visible ? id : undefined}
      {...(childIsDisabled ? openHandlers : {})}
    >
      {enhanced}
      {/*
        Portaled to <body> and positioned `fixed` from the trigger's viewport rect, the way
        ActionMenu does it: an absolutely positioned bubble inside the wrapper is clipped by
        any ancestor with `overflow: auto|hidden` (a DataTable cell, a TableScrollRegion;
        #14903, #15355). It wraps only when wider than the viewport: a percentage max-width on a
        `fixed` box resolves against the layout viewport, which excludes a classic scrollbar
        (viewportSize() above), where `100vw` would include it.
      */}
      {visible &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            ref={bubbleRef}
            id={id}
            role="tooltip"
            data-placement={placement}
            style={bubbleStyle}
            className="fixed z-tooltip max-w-[calc(100%-1rem)] break-words bg-primary text-surface-elevated text-caption rounded-control px-2 py-1 animate-overlay-fade-in"
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
